import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, InputAdornment } from "@mui/material";
import {
  AccountType,
  ApplicationFeature,
  CompanyListItemAdminDto,
  CompanyType,
} from "@green0meter/api-client";
import FeatherIcon from "feather-icons-react";
import { debounce } from "lodash";

import { ExportCompaniesButton } from "./components/ExportCompaniesButton";
import { CompanyEditor } from "./components/CompanyEditor";
import CompanyUsers from "./components/CompanyUsers";

import { useDisclose } from "@hooks/useDisclose";
import { Button, Stack, Typography, TextField, Box, Chip } from "@components/base";
import { Table } from "@components/common";
import { TableColumn } from "@components/common/Table";
import { useGetAdminCompanies } from "@api";
import { useUserStore } from "@store/user";
import { DEFAULT_PAGE_SIZE } from "@constants";

interface FlattenedCompany extends CompanyListItemAdminDto {
  depth: number;
  hasChildren: boolean;
}

const accountTypeColors: Record<AccountType, "success" | "warning" | "info"> = {
  [AccountType.Real]: "success",
  [AccountType.Test]: "warning",
  [AccountType.Demo]: "info",
};

const AdminEntitiesManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useUserStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeCompany, setActiveCompany] = useState<CompanyListItemAdminDto>();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const { adminCompanies, isPendingGetAdminCompanies, refetch } =
    useGetAdminCompanies(user, {
      pageNumber: currentPage,
      pageSize: pageSize,
      search: debouncedSearch || undefined,
    });

  const {
    isOpen: isAddDrawerOpen,
    onOpen: openAddDrawer,
    onClose: closeAddDrawer,
  } = useDisclose();

  // Debounce search input
  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, 300),
    [],
  );

  useEffect(() => {
    debouncedSetSearch(searchValue);
  }, [searchValue, debouncedSetSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchValue(e.target.value);
  };

  const handleClearSearch = (): void => {
    setSearchValue("");
    setDebouncedSearch("");
  };

  const handleEditCompany = (company: CompanyListItemAdminDto): void => {
    setActiveCompany(company);
  };

  const handleCloseEditor = (): void => {
    setActiveCompany(undefined);
  };

  const handleEditorSuccess = (): void => {
    refetch();
  };

  const handleCreateSuccess = (): void => {
    closeAddDrawer();
    refetch();
  };

  const toggleRowExpansion = (companyId: string): void => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(companyId)) {
        newSet.delete(companyId);
      } else {
        newSet.add(companyId);
      }

      return newSet;
    });
  };

  // Flatten data to include expanded subsidiaries
  const flattenedData = useMemo((): FlattenedCompany[] => {
    const result: FlattenedCompany[] = [];

    const flattenCompany = (
      company: CompanyListItemAdminDto,
      depth: number,
    ): void => {
      const hasChildren = (company.subsidiaries?.length ?? 0) > 0;

      result.push({ ...company, depth, hasChildren });

      if (expandedRows.has(company.id) && company.subsidiaries) {
        for (const subsidiary of company.subsidiaries) {
          flattenCompany(subsidiary, depth + 1);
        }
      }
    };

    for (const company of adminCompanies?.items ?? []) {
      flattenCompany(company, 0);
    }

    return result;
  }, [adminCompanies, expandedRows]);

  const columns: TableColumn<FlattenedCompany>[] = [
    {
      title: "",
      key: "expand",
      tableCellProps: { width: 40, padding: "none" },
      renderCellContent: (record) => {
        if (!record.hasChildren) {
          return <Box width={40} />;
        }
        const isExpanded = expandedRows.has(record.id);

        return (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggleRowExpansion(record.id);
            }}
          >
            <FeatherIcon
              icon={isExpanded ? "chevron-down" : "chevron-right"}
              width="16"
              height="16"
            />
          </IconButton>
        );
      },
    },
    {
      title: t("entities.table.column.name"),
      key: "name",
      renderCellContent: (record) => (
        <Box sx={{ pl: record.depth * 3 }}>
          {record.depth > 0 && (
            <Typography component="span" sx={{ color: "text.secondary", mr: 1 }}>
              {"\u2514"}
            </Typography>
          )}
          {record.name}
        </Box>
      ),
    },
    {
      title: t("organizationSettings.vat"),
      key: "companyId",
      dataKey: "companyId",
    },
    {
      title: t("admin.accountType"),
      key: "accountType",
      tableCellProps: { width: 120 },
      renderCellContent: (record) => {
        if (record.companyType !== CompanyType.Master) {
          return "-";
        }
        const accountType = record.accountType ?? AccountType.Real;

        return (
          <Chip
            label={AccountType[accountType]}
            color={accountTypeColors[accountType]}
            size="small"
          />
        );
      },
    },
    {
      title: t("admin.applicationFeatures"),
      key: "features",
      renderCellContent: (record) => {
        const features = record.companyFeatureFunctionalities
          ?.map((cff) => ApplicationFeature[cff.applicationFeature as number])
          .filter((f, i, arr) => f && arr.indexOf(f) === i)
          .join(", ");

        return features || "-";
      },
    },
    {
      key: "actions",
      title: t("common.actions"),
      renderCellContent: (company) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleEditCompany(company)}
          >
            <FeatherIcon icon="edit-3" width="16" height="16" />
          </IconButton>
          <CompanyUsers company={company} />
        </Stack>
      ),
      tableCellProps: { align: "center", width: 100 },
    },
  ];

  return (
    <>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1">{t("organization.entities")}</Typography>
          <Stack direction="row" spacing={2}>
            <ExportCompaniesButton search={debouncedSearch || undefined} />
            <Button
              variant="contained"
              size="small"
              onClick={openAddDrawer}
              startIcon={<FeatherIcon icon="plus" width="16" height="16" />}
            >
              {t("common.add")}
            </Button>
          </Stack>
        </Stack>

        <TextField
          variant="filled"
          size="small"
          placeholder={t("common.search")}
          value={searchValue}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FeatherIcon icon="search" width="16" height="16" />
              </InputAdornment>
            ),
            endAdornment: searchValue && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <FeatherIcon icon="x" width="16" height="16" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Table<FlattenedCompany>
          columns={columns}
          data={flattenedData}
          isLoading={isPendingGetAdminCompanies}
          dataPlaceholder={<Box p={2}>{t("entities.table.noData")}</Box>}
          pagination={{
            pageSize: pageSize,
            pageNumber: currentPage,
            totalItems: adminCompanies?.totalCount ?? 0,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setCurrentPage(1);
            },
            onPageNumberChange: (page) => setCurrentPage(page),
          }}
        />
      </Stack>

      {/* Create mode - no company */}
      <CompanyEditor
        isOpen={isAddDrawerOpen}
        onClose={closeAddDrawer}
        onSuccess={handleCreateSuccess}
      />

      {/* Edit mode - with company */}
      <CompanyEditor
        isOpen={!!activeCompany}
        company={activeCompany}
        onClose={handleCloseEditor}
        onSuccess={handleEditorSuccess}
      />
    </>
  );
};

export default AdminEntitiesManagement;
